import { Component, Input } from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { Recipient } from '../../../interfaces/recipient';
import { Donor } from '../../../interfaces/donor';
import { CommonModule } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { TimelineHistory } from '../../../interfaces/timeline';
import { TimelineService } from '../../../services/timeline.service';
import { Participant } from '../../../interfaces/participant';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { ParticipantDeleteDialogComponent } from '../../participant/participant-delete-dialog/participant-delete-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ParticipantEditComponent } from '../../participant/participant-edit/participant-edit.component';
import { ParticipantService } from '../../../services/participant.service';
import { RequestPriorityComponent } from '../../approvals/request-priority/request-priority.component';
import { AuthService } from '../../../auth/auth.service';
import { Permission } from '../../../interfaces/permission';
import { Router } from '@angular/router';
import { MatchingPeriodService } from '../../../services/matchingPeriod.service';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-participant-history-box',
  imports: [MatIconModule, CommonModule, MatExpansionModule],
  templateUrl: './participant-history-box.component.html',
  styleUrls: ['./participant-history-box.component.scss']
})
export class ParticipantHistoryBoxComponent {
  @Input() donor?: Donor;
  @Input() recipient?: Recipient;
  @Input() timelineHistory?: TimelineHistory[];

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private timelineService: TimelineService,
    private snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private participantService: ParticipantService,
    private readonly authService: AuthService,
    private readonly matchingPeriodService: MatchingPeriodService,
    private router: Router
  ) {
    // Register your SVG icons here
    this.matIconRegistry.addSvgIcon('human-male', this.domSanitizer.bypassSecurityTrustResourceUrl('/human-male.svg'));
    this.matIconRegistry.addSvgIcon('human-female', this.domSanitizer.bypassSecurityTrustResourceUrl('/human-female.svg'));
  }

  canAddParticipant: boolean = false;
  canEditParticipant: boolean = false;
  canRemoveParticipant: boolean = false;
  isCenterUser: boolean = false;
  hasMatchrunStarted: boolean = false;

  ngOnInit(): void {
    // Combine permission, user and matching period checks so the same business
    // rules used in the participants list also apply here in the history view.
    combineLatest([
      this.authService.getUser(),
      this.authService.getAllPermissions$(),
      this.matchingPeriodService.getLatestMatchingPeriod()
    ]).subscribe(([user, permissions, period]) => {
      this.canAddParticipant = permissions.includes(Permission.PARTICIPANTS_ADD_INCLUDING_ALTRUISTS);
      this.canEditParticipant = permissions.includes(Permission.PARTICIPANTS_EDIT);
      this.canRemoveParticipant = permissions.includes(Permission.PARTICIPANTS_EDIT);

      this.isCenterUser = user.activeRole.enumName === 'CENTER';

      // Check if at least one matchrun has started in the current period
      this.hasMatchrunStarted = !!(period?.matchRuns && period.matchRuns.length > 0);
    });
  }

  // Check if CENTER user can edit this participant.
  // CENTER users can only edit participants that are selected for transplantation
  // (to confirm transplant). This restriction only applies AFTER the first matchrun has started.
  canCenterUserEditParticipant(participant?: Participant): boolean {
    if (!this.isCenterUser) {
      return true; // Non-CENTER users follow normal permissions
    }
    // No restrictions before the first matchrun
    if (!this.hasMatchrunStarted) {
      return true;
    }
    return !!participant?.selectedForTransplantation;
  }

  // Check if CENTER user can archive this participant.
  // CENTER users can only archive inactive participants.
  // This restriction only applies AFTER the first matchrun has started.
  canCenterUserArchiveParticipant(participant?: Participant): boolean {
    if (!this.isCenterUser) {
      return true; // Non-CENTER users follow normal permissions
    }
    // No restrictions before the first matchrun
    if (!this.hasMatchrunStarted) {
      return true;
    }
    return participant?.active === false;
  }

  get isRecipient(): boolean {
    return !!this.recipient;
  }

  get age(): number {
    const participantDob = this.recipient?.dob || this.donor?.dob;
    if (participantDob) {
      const birthDate = new Date(participantDob);
      const ageDiffMs = Date.now() - birthDate.getTime();
      const ageDate = new Date(ageDiffMs);
      return Math.abs(ageDate.getUTCFullYear() - 1970); // Calculate age
    }
    return 0; // Default if no DOB is provided
  }

  // Check if recipient is inactive
  get isRecipientInactive(): boolean {
    return this.recipient?.active === false;
  }

  // Check if donor is inactive
  get isDonorInactive(): boolean {
    return this.donor?.active === false;
  }

  // Get inactive reason for recipient
  get recipientInactiveReason(): string | undefined {
    return this.recipient?.reasonInactive;
  }

  // Get inactive reason for donor
  get donorInactiveReason(): string | undefined {
    return this.donor?.reasonInactive;
  }

  // Check if donor is altruist
  get isDonorAltruist(): boolean {
    return this.donor?.altruist === true;
  }

  // Check if participant is locked (either in a locked chain or selected for transplantation)
  get isParticipantLocked(): boolean {
    return !!(
      this.recipient?.locked ||
      this.recipient?.selectedForTransplantation ||
      this.donor?.locked ||
      this.donor?.selectedForTransplantation
    );
  }

  // Get the appropriate lock message based on the participant's status
  get lockMessage(): string {
    const isSelectedForTransplant = !!(this.recipient?.selectedForTransplantation || this.donor?.selectedForTransplantation);

    if (isSelectedForTransplant) {
      return 'This participant is selected for transplantation and cannot be modified';
    } else {
      return 'This participant is locked and cannot be modified';
    }
  }

  parseCenterName(value: string): string {
    try {
      // Parse the JSON string
      const parsedArray = JSON.parse(value);

      // Check if the parsed value is an array
      if (Array.isArray(parsedArray)) {
        // If the array is empty, return "Not limited to center"
        if (parsedArray.length === 0) {
          return 'Not limited to specific centers';
        }

        // Map over the array and extract the 'name' property
        return parsedArray
          .map((item) => item.name || 'Unknown') // Default to 'Unknown' if 'name' is missing
          .join(', '); // Join the names with a comma
      } else {
        // If it's not an array, handle it as a single object
        return parsedArray.name || 'Unknown';
      }
    } catch {
      return 'Invalid Data'; // Return 'Invalid Data' if parsing fails
    }
  }

  removeRecipient(participant: Participant): void {
    participant.type = 'Recipient';
    this.removeParticipant(participant);
  }
  removeDonor(participant: Participant): void {
    participant.type = 'Donor';
    this.removeParticipant(participant);
  }

  removeParticipant(participant: Participant): void {
    const dialogRef = this.dialog.open(ParticipantDeleteDialogComponent, {
      data: { participant: participant },
      width: '500px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.confirmed && participant.id != undefined) {
        const reason = result.reason;
        
        if (participant.type === 'Donor') {
          this.participantService.deleteDonor(participant.id, reason).subscribe({
            next: (response) => {
              console.log('Donor deleted:', response);
              this.showNotification(`Donor ${participant.enisNumber} archived: ${reason}`);
              this.donor = response;
              this.router.navigate(['/participants']);
            },
            error: (err) => {
              console.error('Error:', err);
              this.showNotification(err.message);
            }
          });
        } else {
          this.participantService.deleteRecipient(participant.id, reason).subscribe({
            next: (response) => {
              console.log('Recipient deleted:', response);
              this.showNotification(`Recipient ${participant.enisNumber} archived: ${reason}`);
              this.recipient = response;
              this.router.navigate(['/participants']);
            },
            error: (err) => {
              console.error('Error:', err);
              this.showNotification(err.message);
            }
          });
        }
      }
    });
  }

  editRecipient(participant: Participant): void {
    participant.type = 'Recipient';
    this.editParticipant(participant);
  }
  editDonor(participant: Participant): void {
    participant.type = 'Donor';
    this.editParticipant(participant);
  }

  editParticipant(participant: Participant): void {
    const dialogRef = this.dialog.open(ParticipantEditComponent, {
      data: { participant: participant }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.checkRecipient(result)) {
          this.recipient = result;
        } else {
          this.donor = result;
        }
        this.refreshHistory();
        console.log('Updated participant:', result);
      }
    });
  }

  requestPriority(recipient: Recipient) {
    const dialogRef = this.dialog.open(RequestPriorityComponent, {
      data: recipient
    });
  }

  isDonor(participant: Participant): participant is Donor {
    return participant.type === 'Donor';
  }

  checkRecipient(participant: Participant): participant is Recipient {
    return participant.type === 'Recipient';
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  refreshHistory(): void {
    if (this.recipient?.id) {
      this.timelineService.getHistory(this.recipient.id).subscribe((history) => {
        this.timelineHistory = history;
      });
    } else if (this.donor?.id) {
      this.timelineService.getHistory(this.donor.id).subscribe((history) => {
        this.timelineHistory = history;
      });
    }
  }

  /**
   * Get donor HLA Class I (A, B antigens)
   */
  get donorHlaClassI(): string {
    if (!this.donor?.hla) return '';
    const tokens = this.donor.hla.split(' ');
    const drIndex = tokens.findIndex((t) => t.startsWith('DR'));
    if (drIndex !== -1) {
      return tokens.slice(0, drIndex).join(' ');
    }
    return this.donor.hla;
  }

  /**
   * Get donor HLA Class II (DR antigens)
   */
  get donorHlaClassII(): string {
    if (!this.donor?.hla) return '';
    const tokens = this.donor.hla.split(' ');
    const drIndex = tokens.findIndex((t) => t.startsWith('DR'));
    if (drIndex !== -1) {
      return tokens.slice(drIndex).join(' ');
    }
    return '';
  }

  /**
   * Format acceptable blood types as comma-separated for display
   */
  get formattedAcceptableBloodTypes(): string {
    if (!this.recipient?.acceptableBloodTypes) return '';
    return this.recipient.acceptableBloodTypes.replace(/\s+/g, ', ');
  }
}
